/**
 * SyncActivityTimeline — collapsible sub-section showing last 5 sync events
 * with direction arrows (outbound ↑ / inbound ↓).
 *
 * Hidden entirely when no sync activity exists.
 * Expanded by default if any entry has "Failed" status.
 *
 * Plan 192, Epic 04.
 */

import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { ArrowUp, ArrowDown, AlertCircle, ChevronDown } from "lucide-react";
import { useItemSyncActivity } from "@/features/channels/api/listingsHooks";
import { formatSyncTime } from "./ListingStatusPill";
import type { SyncActivityEntry } from "@/features/channels/api/listingsApi";

interface SyncActivityTimelineProps {
  itemCode: string;
}

function getEntryLabel(entry: SyncActivityEntry): string {
  const ch = entry.channel || "channel";
  if (entry.direction === "outbound") {
    if (entry.sync_subtype === "stock" || entry.type === "Stock Push") {
      return `Stock pushed to ${ch}`;
    }
    if (entry.sync_subtype === "price" || entry.type === "Price Push") {
      return `Price pushed to ${ch}`;
    }
    return `Pushed to ${ch}`;
  }
  // Inbound
  if (entry.type === "Orders") return `Orders synced from ${ch}`;
  return `Products synced from ${ch}`;
}

function getEntryValue(entry: SyncActivityEntry): string {
  const count = entry.records_synced || 0;
  if (entry.direction === "outbound" &&
    (entry.sync_subtype === "stock" || entry.type === "Stock Push")) {
    return `${count} units`;
  }
  return `${count} items`;
}

export function SyncActivityTimeline({ itemCode }: SyncActivityTimelineProps) {
  const { data: entries } = useItemSyncActivity(itemCode);

  const hasFailure = useMemo(
    () => entries?.some((e) => e.status === "Failed") ?? false,
    [entries],
  );

  const [expanded, setExpanded] = useState<boolean | null>(null);

  // Auto-expand on first render if there are failures
  const isExpanded = expanded ?? hasFailure;

  // Hidden entirely when no sync activity exists
  if (!entries || entries.length === 0) return null;

  return (
    <div className="mt-4">
      {/* Header — tappable to collapse/expand */}
      <button
        type="button"
        className="flex items-center gap-2 w-full text-left px-1 py-2"
        onClick={() => setExpanded((prev) => !(prev ?? hasFailure))}
      >
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Sync Activity
        </span>
        <ChevronDown
          className={cn(
            "h-3 w-3 text-muted-foreground/60 transition-transform duration-200",
            !isExpanded && "-rotate-90",
          )}
        />
      </button>

      {/* Collapsible content */}
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200",
          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <div className="space-y-0.5">
            {entries.map((entry, i) => (
              <EntryRow key={entry.started_at || i} entry={entry} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function EntryRow({ entry }: { entry: SyncActivityEntry }) {
  const isOutbound = entry.direction === "outbound";
  const isFailed = entry.status === "Failed";
  const label = getEntryLabel(entry);
  const value = getEntryValue(entry);
  const time = entry.started_at ? formatSyncTime(entry.started_at).relative : "";

  return (
    <div className={cn(
      "flex items-center gap-2 px-1 py-1.5 rounded-md text-sm",
      isFailed && "text-destructive",
    )}>
      {/* Direction arrow */}
      {isFailed ? (
        <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
      ) : isOutbound ? (
        <ArrowUp className="h-3.5 w-3.5 text-primary shrink-0" />
      ) : (
        <ArrowDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      )}

      {/* Label */}
      <span className={cn(
        "flex-1 truncate",
        isFailed ? "text-destructive" : isOutbound ? "text-foreground" : "text-muted-foreground",
      )}>
        {isFailed ? `${label} — failed` : label}
      </span>

      {/* Value */}
      <span className="text-muted-foreground text-xs whitespace-nowrap">
        {value}
      </span>

      {/* Timestamp */}
      <span className="text-muted-foreground/70 text-xs whitespace-nowrap">
        {time}
      </span>
    </div>
  );
}
